import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiClient } from "@/api/config/api-client";
import useAuthStore from "@/store/use-auth-store";
import harmonaiLogo from "@/assets/harmonyai-logo.svg";
import LanguageThemeSwitcher from "@/components/ui/language-theme-switcher";

export default function SlackLinkPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuthStore();
  const hasCalled = useRef(false);

  const [status, setStatus] = useState<"loading" | "success" | "error">(
    "loading",
  );
  const [errorMessage, setErrorMessage] = useState("");

  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setErrorMessage("Missing link token. Please run /harmony link again in Slack.");
      return;
    }

    if (!isAuthenticated()) {
      // Save the current URL so we can return after login
      sessionStorage.setItem(
        "slack_link_redirect",
        `${window.location.origin}/slack/link?token=${token}`,
      );
      navigate("/login");
      return;
    }

    if (hasCalled.current) return;
    hasCalled.current = true;

    apiClient
      .post("/slack/link", { token })
      .then(() => {
        setStatus("success");
        sessionStorage.removeItem("slack_link_redirect");
      })
      .catch((err) => {
        setStatus("error");
        setErrorMessage(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to link account. The link may have expired.",
        );
      });
  }, [token, isAuthenticated, navigate]);

  return (
    <div className="fixed inset-0 bg-background">
      <div className="h-full overflow-y-auto">
        <div className="min-h-full flex items-center justify-center py-6 px-4">
          <div className="w-full max-w-md my-auto space-y-4 p-4 md:p-6 bg-card shadow-sm rounded-lg border">
            <div className="flex flex-col items-center gap-6">
              <img
                src={harmonaiLogo}
                alt="ChatHarmony Logo"
                className="h-16 w-auto"
              />

              {status === "loading" && (
                <div className="text-center space-y-2">
                  <h2 className="text-2xl font-bold">
                    Linking your Slack account...
                  </h2>
                  <p className="text-muted-foreground">Please wait</p>
                  <div className="flex justify-center pt-2">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary" />
                  </div>
                </div>
              )}

              {status === "success" && (
                <div className="text-center space-y-3 text-muted-foreground">
                  <div className="text-5xl">&#10003;</div>
                  <h2 className="text-2xl font-bold">Account Linked!</h2>
                  <p className="text-muted-foreground">
                    Your Slack account is now connected to{" "}
                    <strong>{user?.email}</strong>. You can close this page and
                    go back to Slack.
                  </p>
                </div>
              )}

              {status === "error" && (
                <div className="text-center space-y-3">
                  <div className="text-5xl">&#10007;</div>
                  <h2 className="text-2xl font-bold">Linking Failed</h2>
                  <p className="text-muted-foreground">{errorMessage}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-border">
          <LanguageThemeSwitcher />
        </div>
      </div>
    </div>
  );
}
